import { useFetchPendingForms } from '@/api/hooks/useFetchPendingForms'
import { useSubmitPendingForm } from '@/api/hooks/useSubmitPendingForm'
import { FORM_ALREADY_SUBMITTED, type FormEntry } from '@/api/types/Forms'
import { ErrorState } from '@/components/ErrorState'
import { FormRunner } from '@/components/FormRunner'
import ProtectedPage from '@/components/ProtectedPage'
import { isApiRequestError } from '@/errors/ApiRequestError'
import { analytics } from '@/lib/analytics'
import { FlowHeader } from '@/ui/core/flow-header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { router, useLocalSearchParams } from 'expo-router'
import { useEffect, useMemo, useRef } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'
import Images from '#/assets/images/image-map'

const goHome = () => (router.canGoBack() ? router.back() : router.replace('/'))

/** The page's own chrome around whatever it has to say instead of a form. */
const Notice = ({ children }: { children: React.ReactNode }) => (
	<View className="flex-1">
		<FlowHeader onClose={goHome} />
		<PageShell photo={Images.BrandPhoto}>{children}</PageShell>
	</View>
)

export default function FormScreen() {
	const { id } = useLocalSearchParams<{ id: string }>()
	const formId = useMemo(() => Number.parseInt(id ?? '', 10), [id])

	const { pendingForms, isLoading: isLoadingForms } = useFetchPendingForms()
	const form = pendingForms.find((pending) => pending.id === formId)

	const { mutate: submit, isPending: isSubmitting } = useSubmitPendingForm()
	// Once the answers have gone through, nothing on this screen may send them again, however
	// long the navigation away takes.
	const hasSubmittedRef = useRef(false)

	useEffect(() => {
		if (form) {
			analytics.trackEvent('form-started', { formId: form.id, formType: form.formType })
		}
	}, [form])

	const onSubmit = (entries: FormEntry[]) => {
		if (!form || isSubmitting || hasSubmittedRef.current) {
			return
		}
		submit(
			{ id: form.id, entries, definitionVersion: form.definition.version },
			{
				onSuccess: () => {
					hasSubmittedRef.current = true
					analytics.trackEvent('form-submitted', {
						formId: form.id,
						formType: form.formType,
					})
					Toast.show({
						type: 'success',
						text1: 'Your form has been submitted',
					})
					goHome()
				},
				onError: (error) => {
					// Submitted already, from another tab or an earlier tap that did land: there is
					// nothing left to do here but move on, the way a success would.
					if (isApiRequestError(error) && error.code === FORM_ALREADY_SUBMITTED) {
						hasSubmittedRef.current = true
						Toast.show({
							type: 'success',
							text1: 'You have already submitted this form',
						})
						goHome()
						return
					}
					Toast.show({
						type: 'error',
						text1: error.message || 'We could not submit your form, please try again',
					})
				},
			}
		)
	}

	const renderContent = () => {
		if (isLoadingForms) {
			return (
				<View className="flex-1">
					<FlowHeader onClose={goHome} />
					<PageShell photo={Images.BrandPhoto}>
						<VStack gap={16}>
							<Skeleton isLoading className="h-12 w-3/5 max-w-80" />
							<Skeleton isLoading className="h-40 w-full max-w-[480px]" />
						</VStack>
					</PageShell>
				</View>
			)
		}

		if (!form) {
			return (
				<Notice>
					<ErrorState
						kind="gone"
						glyph="form"
						title="Nothing to fill in here"
						line="This form has already been submitted, or it is no longer waiting on you."
						onAction={goHome}
						actionLabel="Back home"
					/>
				</Notice>
			)
		}

		return (
			<FormRunner
				key={form.id}
				definition={form.definition}
				onSubmit={onSubmit}
				isSubmitting={isSubmitting}
				onExit={goHome}
			/>
		)
	}

	return (
		<ProtectedPage>
			<Screen
				preset="fixed"
				className="flex h-full w-full"
				safeAreaEdges={['top', 'bottom']}
			>
				{renderContent()}
			</Screen>
		</ProtectedPage>
	)
}
